import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ANALYTICS_EVENTS } from "analytics";
import { USER_API, toastMsg } from "config";
import { ApiErrorCode } from "config/errorCodes";
import { normalizeId, handleToast, getApiErrorToastMessage, ServerError } from "utils";
import { budgetKeys, projectKeys } from "../../queries";
import { versionKeys } from "../../queries/version.keys";
import { useCallback, useMemo, useRef } from "react";
import { useAnalytics } from "../useAnalytics";
import { useAxios } from "../useAxios";
import { type ScopedRequest, useScopedMutation } from "../useScopedMutation";
import { useProjectAuth } from "../project/useProjectAuth";
import {
  budgetRunStatusQueryKey,
  isBudgetRunActiveInCache,
  markBudgetRerunPending,
  mirrorBudgetRunReceipt,
} from "../budget/useBudgetRunActive";
import { Permissions, type ProjectPermission } from "utils/permissions/permissionConstants";
import {
  BudgetCategory,
  BudgetI,
  BudgetItem,
  BudgetSubCategory,
  BudgetUpdate,
} from "components/dashboard/body/projects/panels/budget/types";
import { BudgetAssumptions } from "components/dashboard/body/projects/panels/budget/BudgetAssumptionsTypes";
import { targetedRerunRefusalMessage } from "components/dashboard/body/projects/panels/budget/targetedRerun";
import {
  clearTargetedRerunCategories,
  saveTargetedRerunCategories,
} from "../budget/targetedRerunNames";
import { recordAcceptedBudgetRun } from "../budget/acceptedBudgetRun";
import {
  addBudgetItemToCache,
  addBudgetCategoryToCache,
  addBudgetSubCategoryToCache,
  BudgetSummaryCache,
  findBudgetItemInCache,
  findSubCategoryInCache,
  patchBudgetItemInCache,
  patchBudgetSubCategoryInCache,
  removeBudgetItemFromCache,
  reorderBudgetCategoriesInCache,
  reorderBudgetItemsInCache,
  reorderBudgetSubCategoriesInCache,
  setBudgetLockedInCache,
  unwrapBudgetApiData,
} from "./budgetCachePatch";
import { pickFields } from "utils/recordSaveQueue";
import { defineSessionRecordSaves } from "lib/record-save-session";
import { useRecordSaveView } from "../useRecordSaveView";

export type BudgetRerunGuard = {
  blocked: boolean;
  onBlocked: () => void;
};

interface UseBudgetMutationsParams {
  projectId: string | number;
  documentId: string | number;
  budgetId?: string;
  /** When `blocked`, `reRunBudgeting.mutate` / `mutateAsync` call `onBlocked` instead of the API. */
  budgetRerunGuard?: BudgetRerunGuard;
  /** Injectable for tests; defaults to useProjectAuth().canProject */
  canProject?: (permission: ProjectPermission) => boolean;
}

export interface ApplyBudgetTemplateParams {
  categories: BudgetCategory[];
  budgetId?: string;
  estimatedShootDays?: number;
  contingencyPercentage?: number;
  bondFeePercentage?: number;
}

export interface UpdateBudgetInfoParams {
  itemId: string;
  update_data: Partial<BudgetItem>;
  /**
   * The line's values from before the edit. Grids write the edit into the cached line before
   * saving, so the cache snapshot can already hold the new value. A failed save restores these.
   */
  previous_data?: Partial<BudgetItem>;
}

/** Saves edits one at a time per record. See RecordSaveQueue for the rollback rules. */
export interface QueuedSaver<Params> {
  /** Shows the edit right away and saves it. A failed save puts the saved values back. */
  mutate: (params: Params) => void;
  /** Same as `mutate`, and settles with the save that carries this edit. */
  mutateAsync: (params: Params) => Promise<void>;
}

export type BudgetLineSaver = QueuedSaver<UpdateBudgetInfoParams>;

/** Account settings saved through the sub-category endpoint (not its name or number). */
export type BudgetSubCategorySettings = Pick<BudgetSubCategory, "locked" | "importance">;
const SUB_CATEGORY_SETTING_FIELDS = ["locked", "importance"] as const;

// One queue per kind of record for the whole app, so remounted views share it.
const budgetLineSaves = defineSessionRecordSaves<BudgetItem>();
const subCategorySettingSaves = defineSessionRecordSaves<BudgetSubCategorySettings>();

export interface UpdateBudgetParams {
  update_data: Partial<BudgetUpdate>;
}

export interface ReRunBudgetingParams extends BudgetAssumptions {
  merge_policy?: string;
  category_ids?: number[];
  /**
   * Client only, never sent: names of `category_ids`, kept for the completion toast once the
   * API accepts the targeted run.
   */
  category_names?: string[];
}

/**
 * The draft and budget a request was made in. Every budget request carries it (see
 * useScopedMutation), so the request, its cache work and its refresh all stay on that budget.
 */
interface BudgetScope {
  pid: string;
  did: string;
  budgetId: string | undefined;
}

/** A request's budget id, or the error every budget write has always thrown without one. */
function budgetIdOf(target: BudgetScope): string {
  if (!target.budgetId) throw new Error("Budget ID is not available");
  return target.budgetId;
}

const summaryKeyOf = (target: BudgetScope): QueryKey => budgetKeys.summary(target.pid, target.did);

export interface UpdateBudgetSubCategoryParams {
  budget_sub_category_id: number;
  update: Partial<BudgetSubCategorySettings>;
}

export interface CreateBudgetItemParams {
  subCategoryId: number;
  item: Partial<BudgetItem> & { name: string };
}

export interface DeleteBudgetItemParams {
  itemId: number;
}

export interface DuplicateBudgetItemParams {
  itemId: number;
}

export interface ReorderBudgetItemsParams {
  items: Array<{ item_id: number; sort_order: number; parent_item_id?: number | null }>;
}

export interface CreateBudgetCategoryParams {
  name: string;
  fringe_account_number?: string;
}

export interface UpdateBudgetCategoryParams {
  categoryId: number;
  update: Partial<Pick<BudgetCategory, "name" | "fringe_account_number">>;
}

export interface DeleteBudgetCategoryParams {
  categoryId: number;
}

export interface ReorderBudgetCategoriesParams {
  categories: Array<{ category_id: number; sort_order: number }>;
}

export interface CreateBudgetSubCategoryParams {
  categoryId: number;
  name: string;
  account_number: string;
}

export interface UpdateBudgetSubCategoryStructureParams {
  subCategoryId: number;
  update: Partial<Pick<BudgetSubCategory, "name" | "account_number">>;
}

export interface DeleteBudgetSubCategoryParams {
  subCategoryId: number;
}

export interface ReorderBudgetSubCategoriesParams {
  sub_categories: Array<{ sub_category_id: number; sort_order: number }>;
}

export interface ImportBudgetFileParams {
  contents: string;
  filename?: string;
  format?: "auto" | "mmb_xml" | "mmb_json" | "rivetai_xml";
}

export interface ImportBudgetFileResult {
  budget: BudgetI;
  warnings: string[];
  sourceFormat: string;
}

export interface UpdateBudgetVersionParams {
  budgetId: string;
  update: { primary?: boolean; version_name?: string };
}

export interface SetBudgetLockParams {
  locked: boolean;
}

/** 4xx: API or mapped message. 5xx, network, or non-API errors: generic text, never raw server text. */
function toastBudgetMutationError(error: unknown): void {
  handleToast(getApiErrorToastMessage(error, toastMsg.genericServerErr), "error");
}

/** Same text the API sends with BUDGET_RERUN_IN_PROGRESS, used if the response has no message. */
const BUDGET_RERUN_IN_PROGRESS_MESSAGE =
  "A budget re-run is already in progress. Wait for it to finish, then try again. No credits were used.";

/** The API refused a re-run because one is already running for this budget. */
function isBudgetRerunInProgressError(error: unknown): boolean {
  const serverError =
    error instanceof ServerError
      ? error
      : isAxiosError(error)
        ? ServerError.fromAxiosError(error)
        : null;
  return serverError?.code === ApiErrorCode.BUDGET_RERUN_IN_PROGRESS;
}

export const useBudgetMutations = ({
  projectId,
  documentId,
  budgetId,
  budgetRerunGuard,
  canProject: canProjectOverride,
}: UseBudgetMutationsParams) => {
  const queryClient = useQueryClient();
  const axios = useAxios({ allResponse: false });
  const { trackEvent } = useAnalytics();
  const { canProject: canProjectFromAuth } = useProjectAuth(projectId);
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);
  const budgetRerunGuardRef = useRef(budgetRerunGuard);
  budgetRerunGuardRef.current = budgetRerunGuard;
  // Set synchronously on submit. Mutation state only updates on the next render, so a fast
  // double click would otherwise send two re-runs.
  const rerunRequestInFlightRef = useRef(false);
  const canProjectRef = useRef(canProjectOverride ?? canProjectFromAuth);
  canProjectRef.current = canProjectOverride ?? canProjectFromAuth;

  const budgetScope = `${pid}/${did}/${budgetId ?? ""}`;
  const saveView = useRecordSaveView(budgetScope);

  /**
   * The draft and budget open in this render. The budget page stays mounted across a draft
   * switch, and React Query hands a request each new render's callbacks until it settles (also
   * while it waits offline), so every request carries the scope it was made in and every
   * callback reads it from there, never from the render.
   */
  const scope = useMemo<BudgetScope>(() => ({ pid, did, budgetId }), [pid, did, budgetId]);

  const trackBudgetUpdated = useCallback(
    (target: BudgetScope) => {
      trackEvent(ANALYTICS_EVENTS.BUDGET_UPDATED, {
        project_id: String(target.pid),
        document_id: String(target.did),
      });
    },
    [trackEvent]
  );

  const refreshBudgetOf = (target: BudgetScope) =>
    queryClient.invalidateQueries({ queryKey: summaryKeyOf(target) });

  /**
   * A failed optimistic change to the budget reloads the budget it started in. Putting back a
   * snapshot from before the change could erase saves the server confirmed meanwhile.
   */
  const reloadBudgetAfterFailure = (target: BudgetScope) => {
    void refreshBudgetOf(target);
  };

  /** One PATCH of one line. The save queue below decides what reaches the cache. */
  const sendBudgetLineMutation = useScopedMutation(scope, {
    mutationFn: async ({
      variables: params,
      scope: target,
    }: ScopedRequest<{ itemId: string; update_data: Partial<BudgetItem> }, BudgetScope>) => {
      const response = await axios.patch(
        USER_API.updateBudgetInfo(target.pid, target.did, budgetIdOf(target), params.itemId),
        params.update_data
      );
      return unwrapBudgetApiData<BudgetItem>(response);
    },
    onSuccess: (_data, { scope: target }) => trackBudgetUpdated(target),
    onError: (error) => toastBudgetMutationError(error),
  });
  // Bound to the render that queued the edit, so a save sent later still goes to its own draft.
  const sendBudgetLine = sendBudgetLineMutation.mutateAsync;

  const saveBudgetLine = useCallback(
    (params: UpdateBudgetInfoParams): Promise<void> => {
      const itemId = Number(params.itemId);
      const lineKey = budgetKeys.summary(pid, did);
      // A refetch in flight would overwrite the edit on screen.
      void queryClient.cancelQueries({ queryKey: lineKey });
      const cachedLine = findBudgetItemInCache(
        queryClient.getQueryData<BudgetSummaryCache>(lineKey),
        itemId
      );
      return budgetLineSaves(queryClient).save({
        scope: budgetScope,
        record: params.itemId,
        changes: params.update_data,
        // Grids write the edit into the cached line first, so their previous_data wins.
        previous: {
          ...pickFields(cachedLine ?? {}, Object.keys(params.update_data)),
          ...params.previous_data,
        },
        send: (changes) => sendBudgetLine({ itemId: params.itemId, update_data: changes }),
        show: (values) =>
          queryClient.setQueryData<BudgetSummaryCache>(lineKey, (old) =>
            patchBudgetItemInCache(old, itemId, values)
          ),
        refresh: () => queryClient.invalidateQueries({ queryKey: lineKey }),
        view: saveView,
      });
    },
    [budgetScope, did, pid, queryClient, saveView, sendBudgetLine]
  );

  const updateBudgetInfo = useMemo<BudgetLineSaver>(
    () => ({
      mutate: (params) => void saveBudgetLine(params),
      mutateAsync: saveBudgetLine,
    }),
    [saveBudgetLine]
  );

  const updateBudgetMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<UpdateBudgetParams, BudgetScope>): Promise<any> =>
      axios.patch(
        USER_API.getBudget(target.pid, target.did, budgetIdOf(target)),
        params.update_data
      ),
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
      trackBudgetUpdated(target);
    },
    onError: (error) => {
      toastBudgetMutationError(error);
    },
  });

  const updateBudgetCurrencyMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: currency,
      scope: target,
    }: ScopedRequest<string, BudgetScope>): Promise<any> =>
      axios.patch(USER_API.updateBugetCurrency(target.pid, target.did, budgetIdOf(target)), {
        currency,
      }),
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
    },
    onError: (error) => {
      toastBudgetMutationError(error);
    },
  });

  const saveBudgetAssumptionsMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<ReRunBudgetingParams, BudgetScope>): Promise<any> =>
      axios.put(USER_API.saveBudgetRerunParams(target.pid, target.did, budgetIdOf(target)), params),
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(target.pid) });
      if (target.did) {
        queryClient.invalidateQueries({ queryKey: versionKeys.settings(target.pid, target.did) });
      }
      trackBudgetUpdated(target);
    },
    onError: (error) => {
      toastBudgetMutationError(error);
    },
  });

  const reRunBudgetingMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<ReRunBudgetingParams, BudgetScope>): Promise<any> => {
      const { category_names: _clientOnly, ...body } = params;
      return axios.post(
        USER_API.reRunBudgeting(target.pid, target.did, target.budgetId ?? ""),
        body,
        {
          headers: {
            "Idempotency-Key": globalThis.crypto.randomUUID(),
          },
        }
      );
    },
    // No status of its own is written while the POST is out: the caller shows progress on its
    // button, and the run status cache only ever holds what the server reported.
    onSuccess: async (response, { variables: params, scope: target }) => {
      // The guard counts the run as active from the server's start time until a cache shows
      // it ended, so a fresh "completed" from before this run cannot re-enable a second one.
      markBudgetRerunPending(target.pid, target.did, response);
      // The run sync reports this run's end from its own status row, matched by run id, even if
      // that row was cached before this reply or no "rerunning" row is ever seen.
      recordAcceptedBudgetRun(target.pid, target.did, response);
      // Saved here, not in the panel, so the completion toast names them wherever the user is.
      // A full rerun clears them, so it never finishes with an older targeted run's names.
      if (params.category_ids?.length) {
        saveTargetedRerunCategories(target.pid, target.did, params.category_names ?? []);
      } else {
        clearTargetedRerunCategories(target.pid, target.did);
      }
      // The backend writes "rerunning" before it replies. Mirror its receipt into the cached row
      // now, so the global watcher sees the run even if this view is gone, then load the row.
      // A GET sent before that write must not land on top: cancel it (and its revert) first.
      await queryClient.cancelQueries({
        queryKey: budgetRunStatusQueryKey(target.pid, target.did),
      });
      mirrorBudgetRunReceipt(queryClient, target.pid, target.did, response);
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(target.pid) });
    },
    onError: (error, { variables: params, scope: target }) => {
      if (isBudgetRerunInProgressError(error)) {
        // A run is active (another tab or person). Tell the user calmly and load the real
        // status; the server status the refresh returns decides, so no marker is set here.
        handleToast(getApiErrorToastMessage(error, BUDGET_RERUN_IN_PROGRESS_MESSAGE), "info");
        void queryClient.invalidateQueries({ queryKey: projectKeys.detail(target.pid) });
        return;
      }
      // The targeted rerun banner explains this refusal next to the selection.
      if (params.category_ids?.length && targetedRerunRefusalMessage(error)) return;
      toastBudgetMutationError(error);
    },
    onSettled: () => {
      rerunRequestInFlightRef.current = false;
    },
  });

  const reRunBudgeting = useMemo(() => {
    const base = reRunBudgetingMutation;
    // Read at call time, so a confirmation opened before another run started is still refused.
    // Same rule the assumptions dialog uses to disable AI Rebudget.
    const isBudgetRunActive = () => isBudgetRunActiveInCache(queryClient, pid, did);
    return {
      ...base,
      mutate: (variables: ReRunBudgetingParams, options?: Parameters<typeof base.mutate>[1]) => {
        const g = budgetRerunGuardRef.current;
        if (g?.blocked) {
          g.onBlocked();
          return;
        }
        if (rerunRequestInFlightRef.current) return;
        if (isBudgetRunActive()) {
          handleToast(BUDGET_RERUN_IN_PROGRESS_MESSAGE, "info");
          return;
        }
        rerunRequestInFlightRef.current = true;
        base.mutate(variables, options);
      },
      mutateAsync: async (
        variables: ReRunBudgetingParams,
        options?: Parameters<typeof base.mutateAsync>[1]
      ) => {
        const g = budgetRerunGuardRef.current;
        if (g?.blocked) {
          g.onBlocked();
          throw new Error("BUDGET_RERUN_BLOCKED");
        }
        if (rerunRequestInFlightRef.current) {
          throw new Error("BUDGET_RERUN_ALREADY_SUBMITTED");
        }
        if (isBudgetRunActive()) {
          handleToast(BUDGET_RERUN_IN_PROGRESS_MESSAGE, "info");
          throw new Error(ApiErrorCode.BUDGET_RERUN_IN_PROGRESS);
        }
        rerunRequestInFlightRef.current = true;
        return base.mutateAsync(variables, options);
      },
    };
  }, [reRunBudgetingMutation, queryClient, pid, did]);

  /** One PATCH of one account's settings. The save queue below decides what reaches the cache. */
  const sendSubCategorySettingsMutation = useScopedMutation(scope, {
    mutationFn: async ({
      variables: params,
      scope: target,
    }: ScopedRequest<UpdateBudgetSubCategoryParams, BudgetScope>) => {
      const response = await axios.patch(
        USER_API.updateBudgetSubCategory(
          target.pid,
          target.did,
          budgetIdOf(target),
          params.budget_sub_category_id.toString()
        ),
        params.update
      );
      return unwrapBudgetApiData<BudgetSubCategory>(response);
    },
    onError: (error) => toastBudgetMutationError(error),
  });
  // Bound to the render that queued the edit, so a save sent later still goes to its own draft.
  const sendSubCategorySettings = sendSubCategorySettingsMutation.mutateAsync;

  const saveSubCategorySettings = useCallback(
    (params: UpdateBudgetSubCategoryParams): Promise<void> => {
      const subCategoryId = params.budget_sub_category_id;
      const accountKey = budgetKeys.summary(pid, did);
      void queryClient.cancelQueries({ queryKey: accountKey });
      const cachedAccount = findSubCategoryInCache(
        queryClient.getQueryData<BudgetSummaryCache>(accountKey),
        subCategoryId
      );
      return subCategorySettingSaves(queryClient).save({
        scope: budgetScope,
        record: String(subCategoryId),
        changes: params.update,
        previous: pickFields(cachedAccount ?? {}, Object.keys(params.update)),
        send: async (changes) => {
          const saved = await sendSubCategorySettings({
            budget_sub_category_id: subCategoryId,
            update: changes,
          });
          // The server answers with the whole account; only these fields belong to this save.
          return pickFields(saved ?? {}, SUB_CATEGORY_SETTING_FIELDS);
        },
        show: (values) =>
          queryClient.setQueryData<BudgetSummaryCache>(accountKey, (old) =>
            patchBudgetSubCategoryInCache(old, subCategoryId, values)
          ),
        refresh: () => queryClient.invalidateQueries({ queryKey: accountKey }),
        view: saveView,
      });
    },
    [budgetScope, did, pid, queryClient, saveView, sendSubCategorySettings]
  );

  const updateBudgetSubCategory = useMemo<QueuedSaver<UpdateBudgetSubCategoryParams>>(
    () => ({
      mutate: (params) => void saveSubCategorySettings(params),
      mutateAsync: saveSubCategorySettings,
    }),
    [saveSubCategorySettings]
  );

  const createBudgetItemMutation = useScopedMutation(scope, {
    mutationFn: async ({
      variables: params,
      scope: target,
    }: ScopedRequest<CreateBudgetItemParams, BudgetScope>) => {
      const response = await axios.post(
        USER_API.createBudgetItem(
          target.pid,
          target.did,
          budgetIdOf(target),
          String(params.subCategoryId)
        ),
        params.item
      );
      return unwrapBudgetApiData<BudgetItem>(response);
    },
    onSuccess: (data, { variables: params, scope: target }) => {
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        addBudgetItemToCache(old, params.subCategoryId, data)
      );
      trackBudgetUpdated(target);
    },
    onError: (error) => toastBudgetMutationError(error),
  });

  const deleteBudgetItemMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<DeleteBudgetItemParams, BudgetScope>): Promise<void> =>
      axios.delete(
        USER_API.deleteBudgetItem(target.pid, target.did, budgetIdOf(target), String(params.itemId))
      ),
    onMutate: async ({ variables: params, scope: target }) => {
      await queryClient.cancelQueries({ queryKey: summaryKeyOf(target) });
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        removeBudgetItemFromCache(old, params.itemId)
      );
    },
    onSuccess: (_data, { scope: target }) => {
      trackBudgetUpdated(target);
    },
    onError: (error, { scope: target }) => {
      reloadBudgetAfterFailure(target);
      toastBudgetMutationError(error);
    },
  });

  const duplicateBudgetItemMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<DuplicateBudgetItemParams, BudgetScope>): Promise<BudgetItem> =>
      axios.post(
        USER_API.duplicateBudgetItem(
          target.pid,
          target.did,
          budgetIdOf(target),
          String(params.itemId)
        )
      ),
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
      trackBudgetUpdated(target);
    },
    onError: (error) => toastBudgetMutationError(error),
  });

  const reorderBudgetItemsMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<ReorderBudgetItemsParams, BudgetScope>): Promise<void> =>
      axios.patch(USER_API.reorderBudgetItems(target.pid, target.did, budgetIdOf(target)), params),
    onMutate: async ({ variables: params, scope: target }) => {
      await queryClient.cancelQueries({ queryKey: summaryKeyOf(target) });
      const order = [...params.items]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((entry) => entry.item_id);
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        reorderBudgetItemsInCache(old, order)
      );
    },
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
      trackBudgetUpdated(target);
    },
    onError: (error, { scope: target }) => {
      reloadBudgetAfterFailure(target);
      toastBudgetMutationError(error);
    },
  });

  const createBudgetCategoryMutation = useScopedMutation(scope, {
    mutationFn: async ({
      variables: params,
      scope: target,
    }: ScopedRequest<CreateBudgetCategoryParams, BudgetScope>) => {
      const response = await axios.post(
        USER_API.createBudgetCategory(target.pid, target.did, budgetIdOf(target)),
        params
      );
      return unwrapBudgetApiData<BudgetCategory>(response);
    },
    onSuccess: (category, { scope: target }) => {
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        addBudgetCategoryToCache(old, {
          ...category,
          sub_categories: category.sub_categories ?? [],
        })
      );
      trackBudgetUpdated(target);
    },
    onError: (error) => toastBudgetMutationError(error),
  });

  const updateBudgetCategoryMutation = useScopedMutation(scope, {
    mutationFn: async ({
      variables: params,
      scope: target,
    }: ScopedRequest<UpdateBudgetCategoryParams, BudgetScope>) => {
      const response = await axios.patch(
        USER_API.updateBudgetCategory(
          target.pid,
          target.did,
          budgetIdOf(target),
          String(params.categoryId)
        ),
        params.update
      );
      return unwrapBudgetApiData<BudgetCategory>(response);
    },
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
      trackBudgetUpdated(target);
    },
    onError: (error) => toastBudgetMutationError(error),
  });

  const deleteBudgetCategoryMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<DeleteBudgetCategoryParams, BudgetScope>): Promise<void> =>
      axios.delete(
        USER_API.deleteBudgetCategory(
          target.pid,
          target.did,
          budgetIdOf(target),
          String(params.categoryId)
        )
      ),
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
      trackBudgetUpdated(target);
    },
    onError: (error) => toastBudgetMutationError(error),
  });

  const reorderBudgetCategoriesMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<ReorderBudgetCategoriesParams, BudgetScope>): Promise<void> =>
      axios.patch(
        USER_API.reorderBudgetCategories(target.pid, target.did, budgetIdOf(target)),
        params
      ),
    onMutate: async ({ variables: params, scope: target }) => {
      await queryClient.cancelQueries({ queryKey: summaryKeyOf(target) });
      const order = [...params.categories]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((entry) => entry.category_id);
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        reorderBudgetCategoriesInCache(old, order)
      );
    },
    onError: (error, { scope: target }) => {
      reloadBudgetAfterFailure(target);
      toastBudgetMutationError(error);
    },
  });

  const createBudgetSubCategoryMutation = useScopedMutation(scope, {
    mutationFn: async ({
      variables: params,
      scope: target,
    }: ScopedRequest<CreateBudgetSubCategoryParams, BudgetScope>) => {
      const response = await axios.post(
        USER_API.createBudgetSubCategory(
          target.pid,
          target.did,
          budgetIdOf(target),
          String(params.categoryId)
        ),
        { name: params.name, account_number: params.account_number }
      );
      return unwrapBudgetApiData<BudgetSubCategory>(response);
    },
    onSuccess: (subCategory, { variables: params, scope: target }) => {
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        addBudgetSubCategoryToCache(old, params.categoryId, {
          ...subCategory,
          items: subCategory.items ?? [],
        })
      );
    },
    onError: (error) => toastBudgetMutationError(error),
  });

  const updateBudgetSubCategoryStructureMutation = useScopedMutation(scope, {
    mutationFn: async ({
      variables: params,
      scope: target,
    }: ScopedRequest<UpdateBudgetSubCategoryStructureParams, BudgetScope>) => {
      const response = await axios.patch(
        USER_API.updateBudgetSubCategoryStructure(
          target.pid,
          target.did,
          budgetIdOf(target),
          String(params.subCategoryId)
        ),
        params.update
      );
      return unwrapBudgetApiData<BudgetSubCategory>(response);
    },
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
    },
    onError: (error) => toastBudgetMutationError(error),
  });

  const deleteBudgetSubCategoryMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<DeleteBudgetSubCategoryParams, BudgetScope>): Promise<void> =>
      axios.delete(
        USER_API.deleteBudgetSubCategory(
          target.pid,
          target.did,
          budgetIdOf(target),
          String(params.subCategoryId)
        )
      ),
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
    },
    onError: (error) => toastBudgetMutationError(error),
  });

  const reorderBudgetSubCategoriesMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<ReorderBudgetSubCategoriesParams, BudgetScope>): Promise<void> =>
      axios.patch(
        USER_API.reorderBudgetSubCategories(target.pid, target.did, budgetIdOf(target)),
        params
      ),
    onMutate: async ({ variables: params, scope: target }) => {
      await queryClient.cancelQueries({ queryKey: summaryKeyOf(target) });
      const order = [...params.sub_categories]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((entry) => entry.sub_category_id);
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        reorderBudgetSubCategoriesInCache(old, order)
      );
    },
    onError: (error, { scope: target }) => {
      reloadBudgetAfterFailure(target);
      toastBudgetMutationError(error);
    },
  });

  const setBudgetLockMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<SetBudgetLockParams, BudgetScope>): Promise<unknown> =>
      axios.patch(USER_API.lockBudget(target.pid, target.did, budgetIdOf(target)), {
        locked: params.locked,
      }),
    onMutate: async ({ variables: params, scope: target }) => {
      await queryClient.cancelQueries({ queryKey: summaryKeyOf(target) });
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        setBudgetLockedInCache(old, params.locked)
      );
    },
    onSuccess: (_data, { variables: params }) => {
      handleToast(params.locked ? "Budget locked" : "Budget unlocked", "success");
    },
    onError: (error, { scope: target }) => {
      reloadBudgetAfterFailure(target);
      toastBudgetMutationError(error);
    },
  });

  /**
   * Any version write can change which version is live (the backend promotes the oldest one
   * when none is primary), so the version list and the edited budget reload together. That
   * way the Live badge, the version guards and the budget being edited never disagree.
   */
  const refreshVersionsOf = useCallback(
    (target: BudgetScope) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: summaryKeyOf(target) }),
        queryClient.invalidateQueries({
          queryKey: budgetKeys.versions(target.pid, target.did),
        }),
      ]).then(() => undefined),
    [queryClient]
  );
  const refreshBudgetVersions = useCallback(
    () => refreshVersionsOf(scope),
    [scope, refreshVersionsOf]
  );

  const updateBudgetVersionMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<UpdateBudgetVersionParams, BudgetScope>): Promise<unknown> =>
      axios.patch(USER_API.getBudget(target.pid, target.did, params.budgetId), params.update),
    onError: (error) => toastBudgetMutationError(error),
    onSettled: (_data, _error, { scope: target }) => refreshVersionsOf(target),
  });

  const deleteBudgetVersionMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: targetBudgetId,
      scope: target,
    }: ScopedRequest<string, BudgetScope>): Promise<void> =>
      axios.delete(USER_API.deleteBudgetVersion(target.pid, target.did, targetBudgetId)),
    onError: (error) => toastBudgetMutationError(error),
    onSettled: (_data, _error, { scope: target }) => refreshVersionsOf(target),
  });

  const restoreBudgetVersionMutation = useScopedMutation(scope, {
    // The API takes no target: the backend restores into its live version (primary, else the
    // oldest), the same rule as getLiveBudgetVersionId.
    mutationFn: ({
      variables: sourceBudgetId,
      scope: target,
    }: ScopedRequest<string, BudgetScope>): Promise<unknown> =>
      axios.post(USER_API.restoreBudgetVersion(target.pid, target.did, sourceBudgetId)),
    onSuccess: () => handleToast("Budget restored to live version", "success"),
    onError: (error) => toastBudgetMutationError(error),
    onSettled: (_data, _error, { scope: target }) => refreshVersionsOf(target),
  });

  const applyBudgetTemplateMutation = useScopedMutation(scope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<ApplyBudgetTemplateParams, BudgetScope>): Promise<unknown> => {
      if (!canProjectRef.current(Permissions.PROJECT_UPDATE)) {
        throw new Error("BUDGET_TEMPLATE_APPLY_DENIED");
      }

      const estimatedShootDays =
        params.estimatedShootDays && params.estimatedShootDays > 0 ? params.estimatedShootDays : 1;

      const body: Record<string, unknown> = {
        categories: params.categories,
        estimated_shoot_days: estimatedShootDays,
      };

      if (params.budgetId) {
        body.budget_id = Number(params.budgetId);
      }
      if (params.contingencyPercentage != null) {
        body.contingency_percentage = params.contingencyPercentage;
      }
      if (params.bondFeePercentage != null) {
        body.bond_fee_percentage = params.bondFeePercentage;
      }

      return axios.post(USER_API.createBudget(target.pid, target.did), body);
    },
    onSuccess: (_data, { scope: target }) => {
      void refreshBudgetOf(target);
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(target.pid) });
      handleToast("Budget template applied", "success");
    },
    onError: (error: Error) => {
      if (error.message === "BUDGET_TEMPLATE_APPLY_DENIED") {
        handleToast("You do not have permission to update this budget.", "error");
        return;
      }
      toastBudgetMutationError(error);
    },
  });

  const importBudgetFileMutation = useScopedMutation(scope, {
    mutationFn: async ({
      variables: params,
      scope: target,
    }: ScopedRequest<ImportBudgetFileParams, BudgetScope>): Promise<ImportBudgetFileResult> => {
      if (!canProjectRef.current(Permissions.PROJECT_UPDATE)) {
        throw new Error("BUDGET_IMPORT_DENIED");
      }

      const response = await axios.post(USER_API.importBudgetMmbx(target.pid, target.did), {
        contents: params.contents,
        filename: params.filename,
        format: params.format ?? "auto",
      });

      const payload = unwrapBudgetApiData<{
        budget: BudgetI;
        warnings?: string[];
        sourceFormat?: string;
      }>(response);

      return {
        budget: payload.budget,
        warnings: payload.warnings ?? [],
        sourceFormat: payload.sourceFormat ?? "unknown",
      };
    },
    onSuccess: (_data, { scope: target }) => {
      void refreshVersionsOf(target);
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(target.pid) });
    },
    // BudgetImportModal shows the import error with a more specific message, so only the
    // permission refusal is shown here.
    onError: (error: Error) => {
      if (error.message === "BUDGET_IMPORT_DENIED") {
        handleToast("You do not have permission to import a budget.", "error");
      }
    },
  });

  return useMemo(
    () => ({
      updateBudgetInfo,
      updateBudget: updateBudgetMutation,
      updateBudgetCurrency: updateBudgetCurrencyMutation,
      saveBudgetAssumptions: saveBudgetAssumptionsMutation,
      reRunBudgeting,
      updateBudgetSubCategory,
      applyBudgetTemplate: applyBudgetTemplateMutation,
      createBudgetItem: createBudgetItemMutation,
      deleteBudgetItem: deleteBudgetItemMutation,
      duplicateBudgetItem: duplicateBudgetItemMutation,
      reorderBudgetItems: reorderBudgetItemsMutation,
      createBudgetCategory: createBudgetCategoryMutation,
      updateBudgetCategory: updateBudgetCategoryMutation,
      deleteBudgetCategory: deleteBudgetCategoryMutation,
      reorderBudgetCategories: reorderBudgetCategoriesMutation,
      createBudgetSubCategory: createBudgetSubCategoryMutation,
      updateBudgetSubCategoryStructure: updateBudgetSubCategoryStructureMutation,
      deleteBudgetSubCategory: deleteBudgetSubCategoryMutation,
      reorderBudgetSubCategories: reorderBudgetSubCategoriesMutation,
      updateBudgetVersion: updateBudgetVersionMutation,
      deleteBudgetVersion: deleteBudgetVersionMutation,
      restoreBudgetVersion: restoreBudgetVersionMutation,
      refreshBudgetVersions,
      setBudgetLock: setBudgetLockMutation,
      importBudgetFile: importBudgetFileMutation,
    }),
    [
      updateBudgetInfo,
      updateBudgetMutation,
      updateBudgetCurrencyMutation,
      saveBudgetAssumptionsMutation,
      reRunBudgeting,
      updateBudgetSubCategory,
      applyBudgetTemplateMutation,
      createBudgetItemMutation,
      deleteBudgetItemMutation,
      duplicateBudgetItemMutation,
      reorderBudgetItemsMutation,
      createBudgetCategoryMutation,
      updateBudgetCategoryMutation,
      deleteBudgetCategoryMutation,
      reorderBudgetCategoriesMutation,
      createBudgetSubCategoryMutation,
      updateBudgetSubCategoryStructureMutation,
      deleteBudgetSubCategoryMutation,
      reorderBudgetSubCategoriesMutation,
      updateBudgetVersionMutation,
      deleteBudgetVersionMutation,
      restoreBudgetVersionMutation,
      refreshBudgetVersions,
      setBudgetLockMutation,
      importBudgetFileMutation,
    ]
  );
};
